import React, { useState } from 'react';
import useCosmosTrackEvent from 'hooks/useCosmosTrackEvent';
import OptOutConfirmationModal from './OptOutConfirmationModal';
import OptOutFeedbackModal, { OptOutFeedbackPayload } from './OptOutFeedbackModal';

export interface OptOutFeedback {
  // The labels of the options the member picked, not their front-end keys.
  // Empty rather than null when nothing was picked, including on skip.
  selectedOptionIds: string[],
  otherText: string | null,
  skipped: boolean,
}

export type OptOutStage = 'confirm' | 'feedback';

interface AutoRenewOptOutJourneyProps {
  show: boolean,
  /**
   * Saves the opt-out on its own, before any feedback is gathered. Resolving
   * false keeps the member on the confirmation step - the caller owns the error.
   */
  onConfirm: () => Promise<boolean>,
  // Called with the feedback only, once the opt-out itself is already saved.
  // Resolves false to leave the survey open so the member can retry.
  onSubmitFeedback: (_feedback: OptOutFeedback) => Promise<boolean>,
  // Told which step was abandoned so the caller can tag the two exits apart.
  onCancel: (_stage: OptOutStage) => void,
  policyEnd?: string,
}

const AutoRenewOptOutJourney = ({ show, onConfirm, onSubmitFeedback, onCancel, policyEnd }: AutoRenewOptOutJourneyProps) => {
  const [stage, setStage] = useState<OptOutStage>('confirm');
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const { cosmosTrackEvent } = useCosmosTrackEvent('AccountSettings');

  const confirm = async () => {
    if (saving) {
      return;
    }

    setSaving(true);
    const saved = await onConfirm();
    setSaving(false);

    // The caller has already shown the failure modal and rewound the toggle.
    if (!saved) {
      return;
    }

    cosmosTrackEvent('AccountSettings_AutoRenew_OptOut_Feedback_Displayed');
    setStage('feedback');
  };

  // Guards both answer paths against a double press creating a second record.
  const submitOnce = async (feedback: OptOutFeedback) => {
    if (submitted) {
      return;
    }

    setSubmitError(false);
    setSubmitted(true);
    const succeeded = await onSubmitFeedback(feedback);

    // Leaves the survey up, answers intact, so the member can just press again.
    if (!succeeded) {
      setSubmitted(false);
      setSubmitError(true);
    }
  };

  const continueWithFeedback = ({ reasons, otherText }: OptOutFeedbackPayload) => {
    if (submitted) {
      return;
    }

    cosmosTrackEvent('AccountSettings_AutoRenew_OptOut_Feedback_Continue', {
      // The text is member-entered, so only report that something was typed in
      reasons,
      otherTextProvided: otherText.length > 0,
    });

    submitOnce({ selectedOptionIds: reasons, otherText: otherText || null, skipped: false });
  };

  const skipFeedback = () => {
    if (submitted) {
      return;
    }

    cosmosTrackEvent('AccountSettings_AutoRenew_OptOut_Feedback_Skipped');
    submitOnce({ selectedOptionIds: [], otherText: null, skipped: true });
  };

  return (
    <>
      <OptOutConfirmationModal
        show={show && stage === 'confirm'}
        onConfirm={confirm}
        onCancel={() => onCancel('confirm')}
        saving={saving}
      />
      {/**
       * Mounted on demand so the survey's own answers reset between visits, but
       * held past the caller hiding it so its fade-out gets to play - the exit
       * doubles as the rewind to the first step for the next visit.
       */}
      {stage === 'feedback' && (
        <OptOutFeedbackModal
          show={show}
          onContinue={continueWithFeedback}
          onSkip={skipFeedback}
          onCancel={() => onCancel('feedback')}
          onExited={() => {
            setStage('confirm');
            setSubmitted(false);
            setSubmitError(false);
          }}
          policyEnd={policyEnd}
          submitted={submitted}
          error={submitError}
        />
      )}
    </>
  );
};

export default AutoRenewOptOutJourney;
